import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Trash from '../../../assets/trashActive.svg';
import PolygonActive from '../../../assets/PolygonActive.svg';
import Polygon from '../../../assets/Polygon.svg';
import { FormattedMessage } from 'react-intl';

const DropdownWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const DropdownHeader = styled.div`
  width: 100%;
  height: 39px;
  border-radius: 8px;
  padding: 11px 15px 10px 15px;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border: 1px solid #E9E9E9;
  background: white;
  z-index: 1;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
`;

const DropdownListContainer = styled.div<{ top: number; left: number; width: number }>`
  position: absolute;
  z-index: 9999;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  max-height: 220px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 14px 14px 21px 15px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const DropdownItem = styled.div`
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;

  &:hover {
    background: #E2E5FF;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #E9E9E9;
  border-radius: 8px;
  outline: none;
  margin-bottom: 10px;
`;

const DropdownItemText = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: #00000080;
  padding: 15px 0;
`;

const SelectedList = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`;

const SelectedBox = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 9px 7px 11px;
  border-radius: 30px;
  background: #ffd88e;
  span {
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 11px;
    color: #000000;
  }
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #6777FF;
  appearance: none;
  display: inline-block;
  position: relative;
  flex: 0 0 auto;

  &:checked {
    background-color: #6777FF;
  }

  &:checked::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -55%) rotate(45deg);
  }
`;

type OptionType = { id: number; name: string };

type MultiSelectDropdownProps = {
  title: string;
  searchPlaceholder?: string;
  options: OptionType[];
  setData?: (selected: number[]) => void;
  data?: number[];
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  title,
  options,
  setData,
  searchPlaceholder,
  data = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const headerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(search.toLowerCase())
  );

  const updateDropdownPosition = () => {
    if (headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const openDropdown = () => {
    updateDropdownPosition();
    setIsOpen(true);
  };

  const handleSelect = (id: number) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleDelete = (id: number) => {
    setSelected(prev => prev.filter(val => val !== id));
  };

  useEffect(() => {
    if (setData) setData(selected);
  }, [selected]);

  useEffect(() => {
    if (data.length > 0 && selected.length === 0) setSelected(data);
  }, [data]);

  useEffect(() => {
    if (isOpen) updateDropdownPosition();
  }, [selected, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();

    const handleScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', handleScrollOrResize);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <DropdownWrapper>
      <DropdownHeader
        ref={headerRef}
        onClick={e => {
          e.stopPropagation();
          isOpen ? setIsOpen(false) : openDropdown();
        }}
      >
        {title}
        {isOpen ? <PolygonActive /> : <Polygon />}
      </DropdownHeader>

      {isOpen &&
        ReactDOM.createPortal(
          <>
            <Overlay onClick={() => setIsOpen(false)} />
            <DropdownListContainer
              top={position.top}
              left={position.left}
              width={position.width}
              onClick={e => e.stopPropagation()}
            >
              <SearchInput
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <DropdownItemText>Suggestions</DropdownItemText>
              {filteredOptions.map(option => {
                const isChecked = selected.includes(option.id);
                return (
                <DropdownItem
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                >
                    <Checkbox
                      checked={isChecked}
                      readOnly
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(option.id);
                      }}
                    />
                  {option.name}
                </DropdownItem>
                );
              })}
              <div style={{ borderTop: '1px solid #E9E9E9', paddingTop: '15px' }}>
                <DropdownItemText>
                  <FormattedMessage id="Authors.AllRoles" defaultMessage="All Roles" />
                </DropdownItemText>
              </div>
            </DropdownListContainer>
          </>,
          document.body
        )}

      {selected.length > 0 && (
        <SelectedList>
          {selected.map(id => {
            const item = options.find(o => o.id === id);
            return (
              <SelectedBox key={id}>
                <span>{item?.name}</span>
                <div onClick={() => handleDelete(id)} style={{ cursor: 'pointer' }}>
                  <Trash />
                </div>
              </SelectedBox>
            );
          })}
        </SelectedList>
      )}
    </DropdownWrapper>
  );
};

export default MultiSelectDropdown;
